import { useEffect } from 'react';

type TSidePanelSide = 'left' | 'right';

const SHORTCUT_KEY_BY_SIDE: Record<TSidePanelSide, string> = {
  left: '[',
  right: ']',
};

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
};

const hasOpenModal = () =>
  document.querySelector('[role="dialog"][aria-modal="true"]') !== null;

/**
 * Binds the app-wide panel shortcuts: `[` toggles a left panel, `]` a right one.
 *
 * Keystrokes with a modifier, or aimed at a text entry, are left alone so the
 * shortcut never eats a bracket someone is typing. A modal dialog also swallows
 * them, so panels never move behind something the user has to dismiss first.
 */
export const useSidePanelShortcut = ({
  side,
  onToggle,
  enabled = true,
}: {
  side: TSidePanelSide;
  onToggle: () => void;
  enabled?: boolean;
}) => {
  useEffect(() => {
    if (!enabled) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== SHORTCUT_KEY_BY_SIDE[side]) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.defaultPrevented || isTypingTarget(event.target)) return;
      if (hasOpenModal()) return;

      event.preventDefault();
      onToggle();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [side, onToggle, enabled]);
};
